import { useParams } from "react-router-dom";
import React from "react";
import { useState, useEffect, useRef } from "react";
import "../styles/personalize.css";
import config from "../config";
import ReactMarkdown from "react-markdown";

const host = config.backendUrl;

/* popup modal for extra information in certain sections */
const Modal = ({ children, onClose }) => {
    const handleOutsideClick = (e) => {
        if (e.target.className === "modal") {
            onClose();
        }
    };

    return (
        <div className="modal" onClick={handleOutsideClick}>
            <div className="modal-content">
                <span className="close" onClick={onClose}>&times;</span>
                {children}
            </div>
        </div>
    );
};


/* this is the angel preview box that, on desktop, should stick to the top when scrolled past and, on mobile, should shrink to the top right corner */
const ScrollingPreview = ({ children, angelPreviewBackground }) => {
    const [isFixed, setIsFixed] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const previewRef = useRef(null);
    const spacerRef = useRef(null);


    /* check if preview should become fixed when scrolled past */
    useEffect(() => {
      const handleScroll = () => {
        if (!previewRef.current || !spacerRef.current) return;
  
        const previewBottom = previewRef.current.offsetTop + previewRef.current.offsetHeight;
  
        const shouldFix = window.scrollY > previewBottom + 200;
        if (shouldFix !== isFixed) {
          setIsFixed(shouldFix);
        }
      };
  
      window.addEventListener("scroll", handleScroll);
      handleScroll(); // call once on mount
      return () => window.removeEventListener("scroll", handleScroll);
    }, [isFixed]);
    
    /* when shrunk preview is clicked on mobile, it should enlarge */
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (previewRef.current && !previewRef.current.contains(event.target)) {
                setIsExpanded(false);
            }
        };

        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    return (
      <>
        <div ref={spacerRef} className="angel-preview-container">
            <div
                ref={previewRef}
                className={`angel-preview ${isFixed ? `fixed ${isExpanded ? 'expanded' : ''}` : ""}`}
                style={{ backgroundImage: `url("${angelPreviewBackground.replace(" ", "\\ ")}")` }}
                onClick={() => {
                    if (isFixed) {
                        setIsExpanded(!isExpanded);
                    }
                }}
            >
                {children}
            </div>
        </div>
      </>
    );
};

export default function Personalize({title}) {

    /* product data, user customizations, and form stages */
    const { productId } = useParams();
    const [product, setProduct] = useState(null);
    const [vars, setVars] = useState(null);
    const [formData, setFormData] = useState({});

    const [stages, setStages] = useState([]);
    const [activeStages, setActiveStages] = useState([]);
    const [step, setStep] = useState("1");
    const [missingFields, setMissingFields] = useState("");

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState(null);

    const [isFixed, setIsFixed] = useState(false);

    const previewSectionRef = useRef(null);
    const inputRefs = useRef({});

    useEffect(() => {
        document.title = title;
        const handleScroll = () => {
          if (!previewSectionRef.current) return;
          const rect = previewSectionRef.current.getBoundingClientRect();
          const isScrolledPast = rect.bottom < 50; // You can tweak this offset
          setIsFixed(isScrolledPast);
        };
      
        window.addEventListener("scroll", handleScroll);
        handleScroll(); // Trigger on load
        return () => window.removeEventListener("scroll", handleScroll);
      }, []);

    useEffect(() => {
        if (isModalOpen) {
            document.body.classList.add('modal-no-scroll');
        } else {
            document.body.classList.remove('modal-no-scroll');
        }

        // Cleanup when the component unmounts
        return () => {
            document.body.classList.remove('body-no-scroll');
        };
    }, [isModalOpen]);

    useEffect(() => {
        fetch(`${host}/api/products/${productId}`, {
            method: "GET",
            headers: {
                "X-API-Key": process.env.REACT_APP_GANDF_KEY
            }
        })
          .then(response => response.json())
          .then(data => {
            // Initialize formData with preview defaults
            const initialFormData = data.personalizations.reduce((acc, variable) => {
              acc[variable.name] = variable.type === 'list' ? [] : '';
              return acc;
            }, {});
            
            /* set initial form data */
            // First, set all defaults
            data.preview.forEach(variable => {
              if (variable.key && variable.default) {
                initialFormData[variable.key] = variable.default;
              }
            });

            // Then, apply conditional overrides
            // data.preview.forEach(variable => {
            //   if (variable.key && variable.conditional) {
            //     const dependsOn = variable.conditional.dependsOn;
            //     const expectedValues = variable.conditional.expectedValues;
            //     console.log(initialFormData[dependsOn])
            //     if (expectedValues.includes(initialFormData[dependsOn])) {
            //       initialFormData[variable.key] = variable.default;
            //     } 
            //   }
            // });

            const initialVars = data.personalizations.map(variable => {
                let isActive = true;
                if (variable.conditional && variable.conditional.dependsOn) {
                    const dependsOn = variable.conditional.dependsOn;
                    const expectedValues = variable.conditional.expectedValues;
                    isActive = expectedValues.includes(initialFormData[dependsOn]);
                }
                return { ...variable, isActive };
            });

            setFormData(initialFormData);
            setVars(data.personalizations);
            setStages(data.stages);

            // Filter out conditional stages that depend on other variables
            const baseStages = data.stages.filter(stage => !stage.conditional || !stage.conditional.dependsOn);
            setActiveStages(baseStages);
            setProduct(data);

          });
      }, [productId]);

    useEffect(() => {
        if (!product || !stages) return;

        const updatedActiveStages = stages.filter(stage => {
            // Include non-conditional stages
            if (!stage.conditional || !stage.conditional.dependsOn) return true;
            
            // Check conditions for conditional stages
            const dependsOn = stage.conditional.dependsOn;
            const expectedValues = stage.conditional.expectedValues;
            return expectedValues.includes(formData[dependsOn]);
        });

        setActiveStages(updatedActiveStages);
    }, [formData, product, stages]);
    

    const handleChange = (name, value) => {
        setFormData((prevData) => {
            if (prevData[name] === value) return prevData;
            const updatedData = { ...prevData, [name]: value };

        // Check conditions for each variable
        const updatedVars = vars.map(variable => {
            if (variable.conditional && variable.conditional.dependsOn) {
                const dependsOn = variable.conditional.dependsOn;
                const expectedValues = variable.conditional.expectedValues;
                if (expectedValues.includes(updatedData[dependsOn])) {
                    return { ...variable, isActive: true };
                } else {
                    return { ...variable, isActive: false };
                }
            }
            return { ...variable, isActive: true };
        });

        setVars(updatedVars);

        return updatedData;
        });
    };

    const handleListChange = (listName, index, fieldName, value) => {
        setFormData((prevData) => {
            const updatedList = [...prevData[listName]];
            updatedList[index] = {
                ...updatedList[index],
                [fieldName]: value
            };
            return {
                ...prevData,
                [listName]: updatedList,
            };
        });
    };

    const renderListField = (variable, listName, itemIndex, field) => {
        switch (field.type) {
            case 'text':
            return (
                <input
                    type="text"
                    autoComplete="off"
                    maxLength={field.maxCharacters}
                    placeholder={field.placeholder ? field.placeholder : ''}
                    value={formData[listName]?.[itemIndex]?.[field.value] || ''}
                    onChange={(e) => handleListChange(listName, itemIndex, field.value, e.target.value)}
                />
            );
        case 'multiple-choice-small':
            return (
                <div className="multiple-choice-small-container">
                    {field.options.map((option, optionIndex) => (
                        <label 
                        key={`${listName}-${itemIndex}-${field.value}-${option.value}-${optionIndex}`}  // Added optionIndex to make key more unique
                        className="multiple-choice-small"
                        >
                            <input
                                type="radio"
                                name={`${listName}-${itemIndex}-${field.value}`}
                                className="multiple-choice-small-radio"
                                value={option.value}
                                checked={formData[listName]?.[itemIndex]?.[field.value] === option.value}
                                onChange={(e) => handleListChange(listName, itemIndex, field.value, e.target.value)}
                            />
                            <span className="multiple-choice-small-label">{option.label}</span>
                        </label>
                    ))}
                </div>
            );
        case 'multiple-choice-image': 
            return (
                <div className="multiple-choice-image-container">
                    {field.options.map((option) => (
                        <label
                            key={`${listName}-${itemIndex}-${field.value}-${option.value}`}
                            className="multiple-choice-image"
                        >
                            <input
                                type="radio"
                                name={`${listName}-${itemIndex}-${field.value}`}
                                className="multiple-choice-image-radio"
                                value={option.value}
                                checked={formData[listName]?.[itemIndex]?.[field.value] === option.value}
                                onChange={(e) => handleListChange(listName, itemIndex, field.value, e.target.value)}
                            />
                            <img src={option.image}
                                alt={option.label}
                                className="multiple-choice-image-image"
                            />
                        </label>
                    ))}
                </div>
            );
            default:
                return null;
        }
    };

      
    const addListItem = (listName, newEntries) => {
        setFormData((prevData) => {
            const updatedList = [...(prevData[listName] || [])];
            if (newEntries < updatedList.length) {
                updatedList.splice(newEntries);
            } else {
                for (let i = updatedList.length; i < newEntries; i++) {
                    updatedList.push({});  // Just push an empty object for the new list item
                }
            }
            return {
                ...prevData,
                [listName]: updatedList,
            };
        });
    };

    const nextStep = () => {
        const requiredFields = vars.filter((variable) => {
            if (!variable.required || variable.stage.toString() !== step) return false;
            if (variable.conditional && variable.conditional.dependsOn) {
            const dependsOn = variable.conditional.dependsOn;
            const expectedValues = variable.conditional.expectedValues;
            return expectedValues.includes(formData[dependsOn]);
            }
            return true;
        });

    
        const missingFields = requiredFields.filter((field) => {
            const value = formData[field.name];

            if (field.stage > step) return false;
    
            // Check if field is missing
            if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
                return true;
            }
    
            if (field.type === 'list') {
                if (!Array.isArray(value) || value.length === 0) return true; // Ensure it's a non-empty list
                
                return value.some(item => {
                    // Check for empty object
                    if (Object.keys(item).length === 0) return true;
    
                    const fieldVars = vars.find(v => v.name === field.name);
                    if (!fieldVars || !fieldVars.fields) return true; // Vars not found
    
                    // Ensure all required fields inside the list item exist
                    const requiredKeys = fieldVars.fields.filter(f => f.required).map(f => f.value);
                    if (!requiredKeys.every(key => key in item)) return true;
    
                    // Ensure all values are valid (not null, undefined, or empty string)
                    return Object.values(item).some(val => val === null || val === undefined || val === '');
                });
            }
    
            return false; // Field is valid
        });
    
        if (missingFields.length) {
            // make the missing field red
            setMissingFields(missingFields.map(field => field.name));

            const firstMissingField = missingFields[0];
            // console.log(inputRefs.current)
            // console.log(firstMissingField)
            const ref = inputRefs.current[firstMissingField.name];

            // console.log("Hi")
            // console.log(ref)
            // console.log(inputRefs)

            if (ref && ref.current) {
                if (window.innerWidth > 768) {
                    ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else {
                    const rect = ref.current.getBoundingClientRect();
                    window.scrollTo({
                        top: window.scrollY + rect.top - window.innerHeight / 2,
                        behavior: 'smooth'
                    });
                }
            }
            return;
        }
        
        const stageNumbers = activeStages.map(stage => stage.stage.toString());
        const currentIndex = stageNumbers.indexOf(step);

        if (currentIndex < stageNumbers.length - 1) {
            setStep(stageNumbers[currentIndex + 1]);
        }
        
    }
    
    const prevStep = () => {
        const stageNumbers = activeStages.map(stage => stage.stage.toString());
        const currentIndex = stageNumbers.indexOf(step);
    
        if (currentIndex > 0) {
            setStep(stageNumbers[currentIndex - 1]);
        }
    }

    const openDB = async () => {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open("GFUserDB", 1); // Explicit version number
    
            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                if (!db.objectStoreNames.contains("products")) {
                    db.createObjectStore("products", { keyPath: "key" });
                }
                if (!db.objectStoreNames.contains("items")) {
                    db.createObjectStore("items", { keyPath: "key" });
                }
            };
    
            request.onsuccess = (event) => resolve(event.target.result);
            request.onerror = (event) => {
                console.error("IndexedDB Error:", event.target.error);
                reject("Failed to open IndexedDB");
            };
        });
    };
    

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Get all required fields
        const requiredFields = vars.filter((variable) => {
            if (!variable.required || variable.stage.toString() !== step) return false;
            if (variable.conditional && variable.conditional.dependsOn) {
            const dependsOn = variable.conditional.dependsOn;
            const expectedValues = variable.conditional.expectedValues;
            return expectedValues.includes(formData[dependsOn]);
            }
            return true;
        });
    
        const missingFields = requiredFields.filter((field) => {
            const value = formData[field.name];

            if (field.stage > step) return false;
    
            // Check if field is missing
            if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
                return true;
            }
    
            if (field.type === 'list') {
                if (!Array.isArray(value) || value.length === 0) return true; // Ensure it's a non-empty list
                
                return value.some(item => {
                    // Check for empty object
                    if (Object.keys(item).length === 0) return true;
    
                    const fieldVars = vars.find(v => v.name === field.name);
                    if (!fieldVars || !fieldVars.fields) return true; // Vars not found
    
                    // Ensure all required fields inside the list item exist
                    const requiredKeys = fieldVars.fields.filter(f => f.required).map(f => f.value);
                    if (!requiredKeys.every(key => key in item)) return true;
    
                    // Ensure all values are valid (not null, undefined, or empty string)
                    return Object.values(item).some(val => val === null || val === undefined || val === '');
                });
            }
    
            return false; // Field is valid
        });
    
        if (missingFields.length) {
            setMissingFields(missingFields.map(field => field.name));

            const firstMissingField = missingFields[0];
            const ref = inputRefs.current[firstMissingField];

            if (ref && ref.current) {
                if (window.innerWidth > 768) {
                    ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                } else {
                    const rect = ref.current.getBoundingClientRect();
                    window.scrollTo({
                        top: window.scrollY + rect.top - 50,
                        behavior: 'smooth'
                    });
                }
            }

            return;
        }
    
        const db = await openDB();

        return new Promise((resolve, reject) => {
            const transaction = db.transaction("products", "readwrite");
            const store = transaction.objectStore("products");
            store.clear();
            const request = store.put({ key: productId, value: formData });

            request.onsuccess = () => {
                resolve(true);
                window.location.href = `/books/${productId}/preview`;
            };
            request.onerror = () => reject("Failed to save choices to IndexedDB");
        });
    };

    const renderInput = (variable) => {
        const isMissing = missingFields.includes(variable.name);

        if (!inputRefs.current[variable.name]) {
            inputRefs.current[variable.name] = React.createRef();
        }

        switch (variable.type) {
          case 'text':
            return (
                <input
                    type="text"
                    ref={inputRefs.current[variable.name]}
                    className={isMissing ? "missing-field" : ""}
                    autoComplete="off"
                    placeholder={variable.placeholder ? variable.placeholder : ''}
                    maxLength={variable.maxCharacters}
                    name={variable.name}
                    value={formData[variable.name] || ''}
                    onChange={(e) => handleChange(variable.name, e.target.value)}
                />
            );
          case 'date':
            return (
              <input
                ref={inputRefs.current[variable.name]}
                type={variable.type === 'date' ? 'date' : 'text'}
                name={variable.name}
                className={isMissing ? "missing-field" : ""}
                value={formData[variable.name] || ''}
                onChange={(e) => handleChange(variable.name, e.target.value)}
              />
            );
          case 'list':
            return (
              <div className={`list-container ${isMissing ? "missing-field" : ""}`} ref={inputRefs.current[variable.name]} >
                <div className="options" ref={inputRefs.current[variable.name]} >
                    {variable.options.map((option) => (
                        <button 
                            key={option.value}
                            onClick={() => addListItem(variable.name, option.value)}
                            className={`button-option ${formData[variable.name]?.length === option.value ? 'button-option-active' : ''}`}
                        >{option.label}</button>
                    ))}
                </div>

                {/* By default add 1 item */}

                {formData[variable.name]?.length === 0 && addListItem(variable.name, 1)}

                {formData[variable.name]?.map((item, index) => (
                    <div key={`${variable.name}-item-${index}`} className="list-item">
                        {variable.fields.map((field, fieldIndex) => (
                            <div key={`${variable.name}-field-${fieldIndex}`}>
                                <label>
                                    {fieldIndex === 0 && formData[variable.name]?.length > 1 && (index + 1) + ". "}
                                    {field.label}
                                </label>
                                {renderListField(variable, variable.name, index, field)}
                            </div>
                        ))}
                    
                    </div>
                ))}
              </div>
            );
          case 'boolean':
            return (
              <input
                type="checkbox"
                className={isMissing ? "missing-field" : ""}
                ref={inputRefs.current[variable.name]}
                name={variable.name}
                checked={formData[variable.name] || false}
                onChange={(e) => handleChange(variable.name, e.target.checked)}
              />
            );
          case 'multiple-choice-large':
            return (
              <div className={`multiple-choice-large-container ${isMissing ? "missing-field" : ""}`} ref={inputRefs.current[variable.name]} >
                {variable.options.map((option) => (
                    <label key={option.value} className="multiple-choice-large">
                        <input
                        type="radio"
                        name={variable.name}
                        className="multiple-choice-large-radio"
                        value={option.value}
                        checked={formData[variable.name] === option.value}
                        onChange={(e) => handleChange(variable.name, e.target.value)}
                        />
                    <span className="multiple-choice-large-label">{option.label}</span>
                  </label>
                ))}
              </div>
            );
            case 'multiple-choice-small':
            return (
              <div className={`multiple-choice-small-container ${isMissing ? "missing-field" : ""}`} ref={inputRefs.current[variable.name]} >
                {variable.options.map((option) => (
                    <label key={option.value} className="multiple-choice-small">
                        <input
                        type="radio"
                        name={variable.name}
                        className="multiple-choice-small-radio"
                        value={option.value}
                        checked={formData[variable.name] === option.value}
                        onChange={(e) => handleChange(variable.name, e.target.value)}
                        />
                    <span className="multiple-choice-small-label">{option.label}</span>
                  </label>
                ))}
              </div>
            );
            case 'multiple-choice-circle':
            return (
              <div className={`multiple-choice-circle-container ${isMissing ? "missing-field" : ""}`} ref={inputRefs.current[variable.name]} >
                {variable.options.map((option) => (
                    <div className="multiple-choice-circle-outline" key={option.value}>
                    <label className="multiple-choice-circle">
                        <input
                        type="radio"
                        name={variable.name}
                        className="multiple-choice-circle-radio"
                        value={option.value}
                        checked={formData[variable.name] === option.value}
                        onChange={(e) => handleChange(variable.name, e.target.value)}
                        />
                    <span className="multiple-choice-circle-label" style={{backgroundColor: option.fill}}>
        
                    </span>
                  </label>
                  </div>
                ))}
              </div>
            );
            case 'multiple-choice-image':
            return (
              <div className={`multiple-choice-image-container ${isMissing ? "missing-field" : ""}`} ref={inputRefs.current[variable.name]} >
                {variable.options.map((option) => (
                    <label key={option.value} className="multiple-choice-image">
                        <input
                        type="radio"
                        name={variable.name}
                        className="multiple-choice-image-radio"
                        value={option.value}
                        checked={formData[variable.name] === option.value}
                        onChange={(e) => handleChange(variable.name, e.target.value)}
                        />
                    <img src={option.image} alt={option.label} className="multiple-choice-image-image" />
                  </label>
                ))}
              </div>
            );
          default:
            return null;
        }
    };
    
    const renderStages = () => {
        // find the current stage in stages
        const currentStage = stages.find(stage => stage.stage.toString() === step);
        
        const stageTitle = currentStage.title 
            ? currentStage.title.replace(/\{(\w+)\}/g, (_, key) => formData[key] || '') 
            : '';
        
                // const stageDescription = currentStage.description && typeof currentStage.description === 'string'
                // ? currentStage.description.replace(/\{(\w+)\}/g, (_, key) => formData[key] || '') 
                // : ;

        let stageDescription = '';
        if (currentStage.description && typeof currentStage.description === 'string') {
            stageDescription = currentStage.description.replace(/\{(\w+)\}/g, (_, key) => formData[key] || '');
        } else if (currentStage.description && typeof currentStage.description === 'object') {
            stageDescription = currentStage.description;
        }

        if (!currentStage) return null;
        return (
            <div className="form-scroll">

            {(stageTitle || stageDescription) && (
                <div className="stage-header">
                    {stageTitle && <p className="stage-title">{stageTitle}</p>}
                    {stageDescription && typeof stageDescription === 'string' && (
                        <p className="stage-description">{stageDescription}</p>
                    )}
                    {stageDescription && typeof stageDescription === 'object' && (

                        <p className="stage-description" style={{cursor: "pointer"}} onClick={() => {
                            setIsModalOpen(true);
                            setModalContent(stageDescription.clickContent);
                        }}>
                            <ReactMarkdown components={{
                                strong: ({node, ...props}) => (
                                <span style={{ color: '#0e528f', textDecoration: 'underline' }} {...props} />
                                )
                            }}>
                                {stageDescription.text}
                            </ReactMarkdown>
                        </p>
                    )}
                </div>
            )}

            <div className="stage-content">
            {vars.map((variable, index) => {
            // Recalculate the active state directly here to be safe
            let isActive = !variable.conditional;
            if (variable.conditional && variable.conditional.dependsOn) {
                const dependsOn = variable.conditional.dependsOn;
                const expectedValues = variable.conditional.expectedValues;
                isActive = isActive || expectedValues.includes(formData[dependsOn]);
            }
            
            return variable.stage.toString() === step && isActive ? (
                <div key={`${variable.name}-${index}`} className="stage-variable">
                {variable.label && (
                    <label>{`${variable.label.replace(/\{(\w+)\}/g, (_, key) => formData[key] || '')}`}</label>
                )}
                {renderInput(variable)}
                </div>
            ) : null;
            })}

            </div>

            </div>   
        );
    }

    const getDisplayNumber = (index) => {
        return index + 1;
    }

    const renderPreview = () => {
        return (
            product.preview.map((variable, index) =>
                variable.image ? (
                <img
                    key={`preview-image-${index}`}
                    className="angel-preview-image"
                    src={
                    variable.image.replace(/{([^{}]*)}/g, (match, key) => {
                        return formData[key];
                    })
                }
                alt={variable.label}
                />
            ) : null
            )
        );
    }

    return (
        <div className="personalize">
            {product ? (
                <>

                    {isModalOpen && (
                        <Modal onClose={() => setIsModalOpen(false)}>
                            <div className="modal-text modal-open">
                                <ReactMarkdown>{modalContent}</ReactMarkdown>
                            </div>
                        </Modal>
                    )}

                    <div className="book-identifier">
                        <span className="book-label">Personalize your book: <span className="book-title"><i>{product.name}</i></span></span>
                    </div>

                    <div className="step-circles">
                        {activeStages.map((stage, index) => (
                            <React.Fragment key={stage.stage}>
                                <div 
                                    className={
                                        step === stage.stage.toString()
                                            ? "step-circle active"
                                            : step > stage.stage.toString()
                                            ? "step-circle completed"
                                            : "step-circle"
                                    }
                                >
                                    {step > stage.stage.toString() ? <span>&#x2713;</span> : getDisplayNumber(index)}
                                </div>
                                {index < activeStages.length - 1 && (
                                    <div 
                                        className={
                                            step > activeStages[index].stage.toString()
                                                ? "step-line completed"
                                                : "step-line"
                                        }
                                    ></div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    <div className="form-container">

                        {(window.innerWidth > 768 || stages.find(stage => stage.stage.toString() === step)?.showPreview) && (
                            <ScrollingPreview angelPreviewBackground={product.previewBackground || ""}>
                                {product.preview.map((variable, index) => {
                                    if (!variable.image) return null;
                                    
                                    // Check if variable has conditional requirements
                                    if (variable.conditional && variable.conditional.dependsOn) {
                                        const dependsOn = variable.conditional.dependsOn;
                                        const expectedValues = variable.conditional.expectedValues;
                                        if (!expectedValues.includes(formData[dependsOn])) {
                                            return null;
                                        }
                                    }
                                    
                                    return (
                                        <img
                                            key={`preview-image-${index}`}
                                            className="angel-preview-image"
                                            src={
                                                encodeURI(variable.image.replace(/{([^{}]*)}/g, (match, key) => {
                                                    return formData[key];
                                                }))
                                            }
                                            alt={variable.label}
                                        />
                                    );
                                })}
                            </ScrollingPreview>
                        )}

                        <div className="form">

                            {/* <span className="missing-field">{missingField}</span> */}

                            <div className="stage">
                                {renderStages()}
                            </div>

                            <div className="buttons">
                                {step !== activeStages[0].stage.toString() && (
                                    <button className="stage-button" onClick={prevStep}>Previous</button>
                                )}
                                {step !== activeStages[activeStages.length - 1].stage.toString() ? (
                                    <button className="stage-button" onClick={nextStep}>Next</button>
                                ) : (
                                    <button className="submit-button" onClick={handleSubmit}>Preview Book</button>
                                )}
                            </div>

                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className="flipbook-container">
                        <div className="loader"></div>
                    </div>
                </>
            )}
        </div>
    );
}